import { useMemo } from 'react';
import { useGroupedProperties } from './useGroupedProperties';
import { useAuth } from '@/context/AuthContext';
import { isManageableProSubscription, needsProUpgrade } from '@/utils/userUtils';

/** Returns the set of property IDs that are locked behind the paywall, or undefined when no paywall applies. */
export const useLockedPropertyIds = (): Set<number> | undefined => {
	const { sharedWithUser } = useGroupedProperties();
	const { user } = useAuth();

	return useMemo(() => {
		if (!needsProUpgrade(user, sharedWithUser.length)) return undefined;
		return new Set(sharedWithUser.slice(1).map((h) => h.id));
	}, [user, sharedWithUser]);
};

/** Returns true when the given property ID is behind the paywall and the user cannot access it. */
export const useIsPropertyLocked = (houseId: number | undefined): boolean => {
	const lockedIds = useLockedPropertyIds();
	return useMemo(() => {
		if (!houseId || !lockedIds) return false;
		return lockedIds.has(houseId);
	}, [houseId, lockedIds]);
};

/**
 * Paywall state for the Pro upgrade prompt: how many shared properties are locked, and whether to
 * surface the upgrade prompt (locked properties exist and there's no manageable subscription to fix).
 * The locked set is exactly the locked shared properties, so its size is the count.
 */
export const useProUpgradeState = (): { lockedSharedCount: number; showUpgradePrompt: boolean } => {
	const { user } = useAuth();
	const lockedIds = useLockedPropertyIds();

	return useMemo(() => {
		const lockedSharedCount = lockedIds?.size ?? 0;
		const showUpgradePrompt = lockedSharedCount > 0 && !isManageableProSubscription(user?.user_subscription);
		return { lockedSharedCount, showUpgradePrompt };
	}, [lockedIds, user]);
};
